import React from 'react';

import { StyleUtil } from 'ui/util';
import classNames from './EABDStepIndicatorBackground.module.scss';

export interface StepIndicatorBackgroundProps {
  readonly numberOfSteps: number;
  readonly numberOfMergedNodes: number;
  readonly currentStepIndex: number;
  readonly style?: React.CSSProperties;
}

export const StepIndicatorBackground = React.memo<StepIndicatorBackgroundProps>((props) => {
  const { numberOfSteps, numberOfMergedNodes, currentStepIndex, style } = props;

  const NumberOfNodes = React.useMemo(() => numberOfSteps * 2, [numberOfSteps]);

  const ShouldNodeBeHiddenCallback = React.useCallback(
    (index: number, stepIndex: number): boolean => {
      const areFirstNodes = index < NumberOfNodes - numberOfMergedNodes;

      if (areFirstNodes) {
        const numberOfDuplicatedFirstSteps = 2;
        return index > stepIndex - numberOfDuplicatedFirstSteps;
      }

      const areLastNodes = stepIndex + numberOfSteps >= index;
      if (areLastNodes) {
        return areLastNodes;
      }

      return false;
    },
    [numberOfSteps, numberOfMergedNodes, NumberOfNodes]
  );

  const RenderAtomCallback = React.useCallback(
    (index: number, stepIndex: number) => {
      const shouldRender = index < NumberOfNodes - numberOfMergedNodes || index >= numberOfMergedNodes;
      if (!shouldRender) {
        return null;
      }

      const atomClassNames: string[] = [classNames.atom];

      if (ShouldNodeBeHiddenCallback(index, stepIndex)) {
        atomClassNames.push(classNames.hidden);
      }

      const shouldBeActive = stepIndex > index + 1;
      if (shouldBeActive) {
        atomClassNames.push(classNames.active);
      }

      return <div className={StyleUtil.mergeClassNames(atomClassNames)} />;
    },
    [numberOfMergedNodes, NumberOfNodes, ShouldNodeBeHiddenCallback]
  );

  const RenderLineCallback = React.useCallback(
    (index: number, stepIndex: number) => {
      const shouldRender = index < NumberOfNodes - numberOfMergedNodes;
      if (!shouldRender) {
        return null;
      }

      const lineClassNames: string[] = [classNames.line];

      const shouldBeActive = stepIndex > index + 1;
      if (shouldBeActive) {
        lineClassNames.push(classNames.active);
      }

      return <div className={StyleUtil.mergeClassNames(lineClassNames)} />;
    },
    [NumberOfNodes, numberOfMergedNodes]
  );

  const RenderAtomContainerCallback = React.useCallback(
    (_item: unknown, index: number) => {
      const atomContainerClassNames: string[] = [classNames.atomContainer];

      const shouldBeContentPositionedToEnd = index >= NumberOfNodes - numberOfMergedNodes;
      if (shouldBeContentPositionedToEnd) {
        atomContainerClassNames.push(classNames.right);
      }

      const atomContainerStyle: React.CSSProperties = {
        width: `${NumberOfNodes}%`,
      };

      return (
        <div key={index} className={StyleUtil.mergeClassNames(atomContainerClassNames)} style={atomContainerStyle}>
          {RenderAtomCallback(index, currentStepIndex)}
          {RenderLineCallback(index, currentStepIndex)}
        </div>
      );
    },
    [numberOfMergedNodes, currentStepIndex, NumberOfNodes, RenderAtomCallback, RenderLineCallback]
  );

  return (
    <div className={classNames.container} style={style}>
      {Array.from({ length: NumberOfNodes }).map(RenderAtomContainerCallback)}
    </div>
  );
});
